import { ButtonGroup, LinkButton, RaisedButton } from "lib/miolo";
import { FORM_ERROR } from "final-form";
import { useField, useForm } from "react-final-form-hooks";
import restartForm from "lib/utils/form/restartForm";
import checkCanSubmit from "../../../lib/utils/form/checkCanSubmit";
import validateFormValuesAgainstSchema from "../../../lib/utils/form/validateFormValuesAgainstSchema";
import * as palette from "../../palette";
import InlineErrorSpan from "../../shared/form/InlineErrorSpan";
import CommentInputTextArea from "../CommentInputTextArea";
import commentInputFormDataShape from "./shapes/commentInputFormDataShape";
import translateServerErrorIntoFriendlyMessage from "./translateServerErrorIntoFriendlyMessage";

interface IIndividualCommentInputFormProps {
  bodyAsPlainText?: string;
  submitButtonText: string;
  autoFocus?: boolean;
  hintAboutSupportMessagesBeingDeleted?: boolean;
  onCancel: () => void;
  onSubmit: (bodyAsPlainText: string) => Promise<any>;
}

export default function IndividualCommentInputForm({
  bodyAsPlainText = "",
  submitButtonText,
  autoFocus,
  hintAboutSupportMessagesBeingDeleted = true,
  onCancel,
  onSubmit,
}: IIndividualCommentInputFormProps) {
  const { form, handleSubmit, submitting, submitError, ...props } = useForm<{
    bodyAsPlainText: string;
  }>({
    onSubmit: async ({ bodyAsPlainText }) => {
      try {
        await onSubmit(bodyAsPlainText);
      } catch (err) {
        if (err instanceof Error) {
          return {
            [FORM_ERROR]: translateServerErrorIntoFriendlyMessage(err),
          };
        }
        return {
          [FORM_ERROR]: "Erro desconhecido",
        };
      }

      return null;
    },
    validate: validateFormValuesAgainstSchema(commentInputFormDataShape),
    initialValues: {
      bodyAsPlainText,
    },
  });

  const bodyAsPlainTextField = useField("bodyAsPlainText", form);
  const canSubmit = submitting ? false : checkCanSubmit(props);

  return (
    <form
      onSubmit={handleSubmit}
      css={{
        display: "grid",
        gridRowGap: 20,
      }}
    >
      <CommentInputTextArea
        {...bodyAsPlainTextField.input}
        autoFocus={autoFocus}
        hintAboutSupportMessagesBeingDeleted={hintAboutSupportMessagesBeingDeleted}
        placeholder="Escreva alguma coisa aqui..."
      />

      <div
        css={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          alignItems: "center",
        }}
      >
        <div>
          {bodyAsPlainTextField.meta.touched && bodyAsPlainTextField.meta.error && (
            <InlineErrorSpan>{bodyAsPlainTextField.meta.error}</InlineErrorSpan>
          )}
        </div>

        <div
          className="font-normal"
          css={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <ButtonGroup stack="horizontally" spacing={20}>
            <LinkButton
              disabled={submitting}
              variant="black"
              onClick={() => {
                restartForm(form);
                onCancel();
              }}
            >
              Cancelar
            </LinkButton>

            <RaisedButton disabled={!canSubmit} type="submit" variant={canSubmit ? "primary" : "disabled"} height={36}>
              {submitting ? "Aguarde..." : submitButtonText}
            </RaisedButton>
          </ButtonGroup>
        </div>
      </div>

      {submitError && (
        <div
          css={{
            padding: 12,
            color: palette.stiletto,
            backgroundColor: palette.vanillaIce,
            borderRadius: 6,
          }}
        >
          <p
            css={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.33,
              letterSpacing: 1,
            }}
          >
            {submitError}
          </p>
        </div>
      )}
    </form>
  );
}
